import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Stack, Button, Typography } from "@mui/material";
import TabIcon from "@mui/icons-material/Tab";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
 
// ─── Config ──────────────────────────────────────────────────────────────────
// IS_LIVE = true  →  tab lock runs everywhere (localhost + production)
// Change to:  window.location.hostname !== "localhost"
// if you want developers to open multiple tabs freely.
const IS_LIVE = true;
 
// ─── Dynamic inactivity timeout based on WalletTypeID ────────────────────────
// WalletTypeID is stored in sessionStorage by OtpPage.js after every login
// (both OTP login and TPIN login paths call handleLoginSuccess which saves it).
// 1 → premium wallet → 20 minutes of allowed inactivity.
// 0 (or anything else) → basic wallet → 10 minutes.
// Reading this dynamically means the timeout picks up the correct value even
// when the component re-mounts mid-session (e.g. hot-reload in dev), and
// doesn't require the constant to be set at app startup before login.
const getInactivityTimeoutMs = () => {
  const walletTypeId = sessionStorage.getItem("WalletTypeID");
  return walletTypeId === "1"
    ? 20 * 60 * 1000  // 20 minutes for WalletTypeID = 1
    : 10 * 60 * 1000; // 10 minutes for WalletTypeID = 0 (default)
};
// NOTE: there is no client-side SESSION_TIMEOUT_MS anymore. Session expiry
// is decided entirely by the backend (see the comment further below, and
// Utils/Api.js's response interceptor).
 
const CHANNEL_NAME = "tawallet_tab_sync";
const MSG_TAB_OPENED = "TAB_OPENED";
const MSG_TAB_ACTIVE = "TAB_ACTIVE";
const MSG_TAB_CLOSED = "TAB_CLOSED";
 
// Key stored in localStorage so it survives sessionStorage.clear()
const SESSION_EXPIRED_KEY = "SESSION_EXPIRED";
const SESSION_EXPIRED_MSG =
  "Your session has timed out due to inactivity. Please log in again.";
 
const PUBLIC_ROUTES = [
  "/",
  "/login",
  "/verify-otp",
  "/otp-page",
  "/registration",
  "/password",
  "/termsandconditions",
];
 
const isPublicRoute = (pathname) =>
  PUBLIC_ROUTES.some(
    (r) => pathname === r || pathname.startsWith("/registration"),
  );
 
export default function SessionManager() {
  const navigate = useNavigate();
  const location = useLocation();
 
  // ── Session-timeout ──
  const inactivityTimer = useRef(null);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
 
  // ── Duplicate-tab ──
  const [isDuplicateTab, setIsDuplicateTab] = useState(false);
  const channelRef = useRef(null);
  const isActiveTabRef = useRef(false);
 
  // ─── Logout ──────────────────────────────────────────────────────────────
  const logout = (reason = "timeout") => {
    if (channelRef.current) {
      channelRef.current.postMessage({ type: MSG_TAB_CLOSED });
    }
 
    // Preserve theme across the clear
    const theme = localStorage.getItem("themeMode");

    // ✅ FIX: preserve SessionWalletToken in localStorage across a 4-min
    // inactivity logout. Loginpage.js / OtpPage.js read this token from
    // localStorage to decide "is this user's WalletToken still valid? if
    // so, skip mobile-number entry and OTP, go straight to TPIN."
    // Previously this function preserved the token in sessionStorage (a
    // key the very next line then deletes anyway via localStorage.clear()
    // since sessionStorage and localStorage are separate stores) — so by
    // the time the user reached /login again, localStorage had no
    // SessionWalletToken left, and Loginpage.js had no choice but to fall
    // through to the mobile-number form even though the token itself
    // (server-side) was still perfectly valid. Saving it here, in
    // localStorage, and restoring it after the clear, is what lets the
    // 4-min-timeout path behave exactly like the normal flow: validate the
    // saved token on the next /login visit, and only show mobile-number
    // entry if that token is actually gone or expired.
    const walletToken = localStorage.getItem("SessionWalletToken");
 
    Object.keys(sessionStorage).forEach((key) => {
      sessionStorage.removeItem(key);
    });
    localStorage.clear();
 
    // Restore preserved values AFTER clear
    if (theme) localStorage.setItem("themeMode", theme);
    if (walletToken !== null) {
      localStorage.setItem("SessionWalletToken", walletToken);
    }
 
    if (reason === "timeout") {
      // ✅ Store flag in localStorage (survives sessionStorage.clear on /login mount)
      localStorage.setItem(SESSION_EXPIRED_KEY, SESSION_EXPIRED_MSG);
      setShowSessionExpired(true); // show the popup before navigating
    } else {
      navigate("/login", { replace: true });
    }
  };
 
  // ─── Dismiss session-expired popup → go to login ─────────────────────────
  const handleSessionExpiredClose = () => {
    setShowSessionExpired(false);
    navigate("/login", { replace: true });
  };
 
  // ─── Inactivity timer ────────────────────────────────────────────────────
  // ⚠️ PERFORMANCE FIX:
  // getInactivityTimeoutMs() calls sessionStorage.getItem() — a synchronous
  // storage read. Previously this was called INSIDE resetInactivityTimer,
  // which fires on every "mousemove" event (hundreds of times per second
  // while the mouse is moving). That caused the whole app to feel sluggish.
  //
  // Fix: read WalletTypeID ONCE into a ref when the effect runs (on
  // mount and pathname change), then reuse that cached value in
  // resetInactivityTimer on every event — zero storage reads per event.
  const timeoutMsRef = useRef(getInactivityTimeoutMs());

  // useCallback keeps resetInactivityTimer's reference stable across
  // renders so the event listener add/remove correctly matches the same
  // function object. Without this, every re-render adds a new listener
  // while only removing the old one via a stale closure.
  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      logout("timeout");
    }, timeoutMsRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // NOTE: The old client-side "1 hour SESSION_TIMEOUT_MS" effect that used
  // to live here has been removed. Session expiry is decided by the
  // BACKEND, not the browser's clock — the backend returns
  // Response_Code "99723" (Session Expired) on whatever API call happens
  // to be in flight when the session actually expires server-side. That
  // signal is handled centrally in Utils/Api.js (the response interceptor),
  // which calls ValidateJwtToken and either extends the session (silently
  // retries the request, with a toast confirming the extension) or logs the
  // user out if the backend says the session can't be extended.
  //
  // The previous client-side timer ran INDEPENDENTLY of that — it just
  // counted 1 hour from LOGIN_TIME and force-logged-out regardless of
  // whether the backend session was still valid or had already been
  // extended. That's what produced "login succeeds, then immediately
  // shows Session Timeout": LOGIN_TIME from a previous session was being
  // preserved across logout, so the new session's age was computed wrong,
  // and even when correct, this timer had no way to know the backend had
  // already extended the session. Removing it entirely is the right fix —
  // the backend is the single source of truth for session expiry.

  // ─── Session-timeout effect (authenticated pages only) ───────────────────
  useEffect(() => {
    const token =
      sessionStorage.getItem("token") || sessionStorage.getItem("mobileNumber");
 
    if (isPublicRoute(location.pathname) || !token) return;

    // Refresh the cached timeout value once per route change — this is
    // the only time we read sessionStorage for WalletTypeID, not on every
    // mousemove event.
    timeoutMsRef.current = getInactivityTimeoutMs();
 
    const events = [
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];
 
    events.forEach((e) =>
      window.addEventListener(e, resetInactivityTimer, { passive: true }),
    );
 
    resetInactivityTimer();
 
    return () => {
      events.forEach((e) =>
        window.removeEventListener(e, resetInactivityTimer),
      );
      clearTimeout(inactivityTimer.current);
    };
  }, [location.pathname, resetInactivityTimer]);
 
  // ─── Duplicate-tab detection ─────────────────────────────────────────────
  useEffect(() => {
    if (!IS_LIVE) return;
 
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;
    isActiveTabRef.current = true;
 
    channel.onmessage = (event) => {
      const { type } = event.data;
 
      if (type === MSG_TAB_OPENED) {
        // Another tab opened — tell it we are already active
        if (isActiveTabRef.current) {
          channel.postMessage({ type: MSG_TAB_ACTIVE });
        }
      }
 
      if (type === MSG_TAB_ACTIVE) {
        // We received a reply — we are the duplicate
        isActiveTabRef.current = false;
        setIsDuplicateTab(true);
      }
 
      if (type === MSG_TAB_CLOSED) {
        // Active tab closed — unblock this tab
        isActiveTabRef.current = true;
        setIsDuplicateTab(false);
      }
    };
 
    // Announce this tab
    channel.postMessage({ type: MSG_TAB_OPENED });
 
    const handleUnload = () => channel.postMessage({ type: MSG_TAB_CLOSED });
    window.addEventListener("beforeunload", handleUnload);
 
    return () => {
      channel.postMessage({ type: MSG_TAB_CLOSED });
      channel.close();
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []); // runs once — channel lives for the full tab lifetime
 
  // ─── "Continue Here" — take over from duplicate tab ──────────────────────
  const handleLogoutHere = () => {
    if (channelRef.current) {
      channelRef.current.postMessage({ type: MSG_TAB_CLOSED });
    }
    const theme = localStorage.getItem("themeMode");
    const walletToken = localStorage.getItem("SessionWalletToken");

    sessionStorage.clear();
    localStorage.clear();

    if (theme) localStorage.setItem("themeMode", theme);
    if (walletToken !== null) {
      localStorage.setItem("SessionWalletToken", walletToken);
    }
 
    isActiveTabRef.current = true;
    setIsDuplicateTab(false);
    navigate("/login", { replace: true });
  };
 
  // ─── Shared styles ────────────────────────────────────────────────────────
  const overlayStyles = {
    position: "fixed",
    inset: 0,
    zIndex: 99999,
    bgcolor: "rgba(0,0,0,0.82)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "all",
  };
 
  const cardStyles = {
    bgcolor: "background.paper",
    borderRadius: 4,
    p: 4,
    maxWidth: 440,
    width: "90%",
    textAlign: "center",
    boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
  };
 
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ══ SESSION EXPIRED POPUP ═══════════════════════════════════════════ */}
      {showSessionExpired && (
        <Box
          sx={overlayStyles}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Box sx={cardStyles}>
            {/* Animated clock icon */}
            <Box
              sx={{
                position: "relative",
                width: 80,
                height: 80,
                mx: "auto",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: -6,
                  borderRadius: "50%",
                  border: "2px solid",
                  borderColor: "warning.light",
                  opacity: 0.5,
                  "@keyframes pulse-ring": {
                    "0%": { transform: "scale(0.9)", opacity: 0.6 },
                    "70%": { transform: "scale(1.15)", opacity: 0 },
                    "100%": { transform: "scale(0.9)", opacity: 0 },
                  },
                  animation: "pulse-ring 2s ease-out infinite",
                }}
              />
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #fff8e1 0%, #ffe082 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid",
                  borderColor: "warning.main",
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 40, color: "#e65100" }} />
              </Box>
            </Box>
 
            <Typography
              variant="h6"
              fontWeight={700}
              gutterBottom
              sx={{ color: "text.primary" }}
            >
              Session Timed Out
            </Typography>
 
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.9, mb: 1 }}
            >
              Your session has expired due to <strong>inactivity</strong>.
            </Typography>
 
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.9, mb: 3.5 }}
            >
              For your security, we've automatically logged you out.
              <br />
              Please log in again to continue using{" "}
              <strong>tawallet.com</strong>.
            </Typography>
 
            <Stack spacing={1.5}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSessionExpiredClose}
                sx={{
                  borderRadius: 3,
                  py: 1.3,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 14,
                  background: "linear-gradient(135deg, #e65100, #ff6d00)",
                  boxShadow: "0 4px 14px rgba(230,81,0,0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #bf360c, #e65100)",
                  },
                }}
              >
                Go to Login
              </Button>
 
              <Typography variant="caption" color="text.disabled">
                You were inactive for {timeoutMsRef.current / 60000} minutes.
              </Typography>
            </Stack>
          </Box>
        </Box>
      )}
 
      {/* ══ DUPLICATE TAB BLOCKER ════════════════════════════════════════════ */}
      {isDuplicateTab && (
        <Box
          sx={overlayStyles}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Box sx={cardStyles}>
            <Box
              sx={{
                width: 76,
                height: 76,
                borderRadius: "50%",
                bgcolor: "#e3f2fd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <TabIcon sx={{ fontSize: 44, color: "#1565c0" }} />
            </Box>
 
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Application Already Open
            </Typography>
 
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.9, mb: 1 }}
            >
              <strong>tawallet.com</strong> is already running in another
              browser tab.
            </Typography>
 
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.9, mb: 3.5 }}
            >
              Only <strong>one tab</strong> is allowed at a time for your
              security.
              <br />
              Please switch to the other tab, or close it first.
              <br />
              If you want to continue here, click below to logout from the other
              tab.
            </Typography>
 
            <Stack spacing={1.5}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleLogoutHere}
                sx={{
                  borderRadius: 3,
                  py: 1.3,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 14,
                  background: "linear-gradient(135deg, #1565c0, #1e88e5)",
                  boxShadow: "0 4px 14px rgba(21,101,192,0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #0d47a1, #1565c0)",
                  },
                }}
              >
                Logout Other Tab &amp; Continue Here
              </Button>
 
              <Typography variant="caption" color="text.disabled">
                This tab is fully blocked until the other tab is closed or
                logged out.
              </Typography>
            </Stack>
          </Box>
        </Box>
      )}
    </>
  );
}